// "As of" dates for the pages that show end-of-day data. Every date is read
// from the API rows, never from the visitor's clock, so a page can't label old
// prices as this month's. Also the SEBI 60-day line, one rule for every page that shows trades.
import { ist } from "../bossai/liveClock";

// Short month names, the one list every page's dates use.
export const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS = MONTH_SHORT;

// "2026-10-13" (API rows) or "13-10-2026" (strategy config) -> [2026, 10, 13]; anything else -> null.
const parts = (s) => {
  if (typeof s !== "string") return null;
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return [Number(m[1]), Number(m[2]), Number(m[3])];
  m = s.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (m) return [Number(m[3]), Number(m[2]), Number(m[1])];
  return null;
};

// The same date as "2026-10-13", or null when the value isn't a date.
export const isoDay = (s) => {
  const p = parts(s);
  return p ? `${p[0]}-${String(p[1]).padStart(2, "0")}-${String(p[2]).padStart(2, "0")}` : null;
};

// "13 Oct 2026", or null when the value isn't a date. No Date object, so no
// time-zone shift turns 13 Oct into 12 Oct.
export const formatAsOf = (s) => {
  const p = parts(s);
  if (!p || p[1] < 1 || p[1] > 12) return null;
  return `${p[2]} ${MONTHS[p[1] - 1]} ${p[0]}`;
};

// The newest date found under `key` in the rows (as the row wrote it), or null.
export const latestDate = (rows, key) => {
  let best = null;
  let bestN = -1;
  (rows || []).forEach((r) => {
    const p = parts(r?.[key]);
    if (!p) return;
    const n = p[0] * 10000 + p[1] * 100 + p[2];
    if (n > bestN) {
      bestN = n;
      best = r[key];
    }
  });
  return best;
};

// A book's last rebalance: its newest trade, either a buy it still holds (live
// entryDate) or a sale (closed exitDate). A live row's exitDate is the date of
// the current price, not a trade, so it is never read here. Null when no
// trade has a date.
export const lastTradeDate = (liveTrades, closedTrades) =>
  latestDate(
    [latestDate(liveTrades, "entryDate"), latestDate(closedTrades, "exitDate")].map((d) => ({ d })),
    "d",
  );

// ---------- The 60-day line (SEBI, owner's rule of 13 Oct 2026) ----------
// A trade that closed MORE than RECENT_DAYS calendar days ago, counted in India, may be shown to a signed-in
// visitor; one that closed on the 60th day back or later, or with no readable exit date, is a subscriber's only.
// Portfolio pages (portfolioView.js), Trade Ideas (tradeIdeasModel.js) and a screen's page all use these.
export const RECENT_DAYS = 60;

// Today's date in India ("2026-10-13"), from the visitor's clock (a number of ms, or a Date). A date string
// ("2026-10-13", e.g. gateToday's answer) is that day.
export const todayIST = (now = Date.now()) =>
  typeof now === "string" ? isoDay(now) : ist(typeof now === "number" ? now : now.getTime()).iso;

// The day the 60-day line counts back from. The page is the only gate (the API sends every closed row, to anyone),
// so a device clock that runs ahead must not move the line forward: the device's day, but never later than the
// newest price date the server sent (an open row's exitDate is the date of its latest price, so it is never after
// the server's today). Without a dated open row, the device's day. Returns "2026-10-13".
export const gateToday = (liveTrades, now = Date.now()) => {
  const device = todayIST(now);
  const server = isoDay(latestDate(liveTrades, "exitDate"));
  return server && (!device || server < device) ? server : device;
};

// Whole days since 1 Jan 1970 for any date isoDay reads ("2026-10-13", "2026-10-13T…", "13-10-2026"); NaN otherwise.
export const dayNumber = (s) => {
  const d = isoDay(s);
  return d ? Date.UTC(Number(d.slice(0, 4)), Number(d.slice(5, 7)) - 1, Number(d.slice(8, 10))) / 86400000 : NaN;
};
export const isoOfDayNumber = (n) => new Date(n * 86400000).toISOString().slice(0, 10);

// Closed more than 60 days before todayIso (61 days or more). An unreadable date is never "older".
export const isOlderThanRecent = (exitDate, todayIso) => {
  const d = dayNumber(exitDate);
  const t = dayNumber(todayIso);
  return Number.isFinite(d) && Number.isFinite(t) && t - d > RECENT_DAYS;
};
